// src/ControlContext.jsx

import React, {
  useEffect,
  useState,
  createContext,
  useMemo,
  useCallback,
  useRef,
  controlsRef,
} from "react";
import * as THREE from "three";
import { withCrossOrigin } from "./utils/ensureTextureCrossOrigin";
import {
  normalizeChoice,
  WIDTH_LABEL_POSITIONS,
  HEIGHT_LABEL_POSITIONS,
  ORIENTATIONS,
} from "./utils/frameMeasurements";
// import { screenshotEventEmitter } from "./events";
export const ControlContext = createContext();

import { materialLoader } from "./Helpers/materialLoader";
import { WOOD } from "./Helpers/WOOD";
import { WOODF } from "./Helpers/WOODF";
import { PAPER } from "./Helpers/PAPER";
import { METAL } from "./Helpers/METAL";
import { METALF } from "./Helpers/METALF";
import { COLORS } from "./Helpers/COLORS";

// import { useLoadingStore } from "./utils/LoadingOrchestrator";

// The passpartout's inner 45-degree insert catches shadow on real mat board.
// Scale the mat color by this factor to render that bevel visibly darker.
const BEVEL_DARKEN = 0.4;

// Safari can cache the same URL separately for different CORS modes.
// Normalize image URLs so every request we make uses the same CORS-friendly variant.
const normalizeImageUrlForCors = (url) => {
  if (!url) return url;

  // Ignore data URLs and other non-http(s) schemes.
  if (/^data:/i.test(url)) return url;

  try {
    const parsed = new URL(url, window?.location?.href);

    // Signed URLs (S3 / GCS) compute their signature over the exact query
    // string — appending corsMode invalidates the signature. Leave them as-is.
    const isSigned =
      parsed.searchParams.has("X-Amz-Signature") ||
      parsed.searchParams.has("X-Goog-Signature") ||
      parsed.searchParams.has("Signature");
    if (isSigned) return url;

    // Stable param so we don't grow the query string on repeated calls.
    if (!parsed.searchParams.has("corsMode")) {
      parsed.searchParams.set("corsMode", "anonymous");
    }
    return parsed.toString();
  } catch (err) {
    console.warn("Unable to normalize image URL for CORS:", err);
    return url;
  }
};

export const ControlProvider = ({ children }) => {
  const [config, setConfig] = useState({});

  /** SCENE SETTINGS */
  const [wallColor, setWallColor] = useState("#E7E7E7");
  const [maxFrameSize, setMaxFrameSize] = useState(200);

  /** CAMERA SETTINGS */

  const [cameraType, setCameraType] = useState("Room");
  // const [cameraZ, setCameraZ] = useState(200);
  // const [camDolly, setCamDolly] = useState(false);
  const [cameraDetailW, setCameraDetailW] = useState();
  const [cameraDetailH, setCameraDetailH] = useState();
  const [cameraDetailX, setCameraDetailX] = useState();
  const [cameraDetailY, setCameraDetailY] = useState();

  const [boundingMesh, setBoundingMesh] = useState(null);
  const [frameViewPadding, setFrameViewPadding] = useState(0.15);

  const [envIntensity, setEnvIntensity] = useState(1);

  /** MEASUREMENTS GIZMO SETTINGS */

  const [showMeasurements, setShowMeasurements] = useState(false);
  const [measurementsPosition, setMeasurementsPosition] = useState(25);
  const [measurementsOrientation, setMeasurementsOrientation] =
    useState("vertical");
  const [totalWidthLabelPosition, setTotalWidthLabelPosition] =
    useState("below");
  const [totalHeightLabelPosition, setTotalHeightLabelPosition] =
    useState("left");

  // const cameraControlsRef = useRef();

  /** PRINT SETTINGS */

  // const [imageFile, setImageFile] = useState("textures/13.jpg");
  const [imageFile, setImageFile] = useState();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [printWidth, setPrintWidth] = useState(75);
  const [printHeight, setPrintHeight] = useState(55);

  // const [aspectRatio, setAspectRatio] = useState(1.36);
  // const [aspectRatioLock, setAspectRatioLock] = useState(true);
  const [paperMaterial, setPaperMaterial] = useState();
  const [paperType, setPaperType] = useState("Paper Matt");
  // const [finishType, setFinishType] = useState("Paper Matt");

  //** BORDER SETTINGS *//

  const [borderWidth, setBorderWidth] = useState(10);
  const [borderHeight, setBorderHeight] = useState(10);
  const originalBorderColor = "#FFFFFF";
  const [borderColor, setBorderColor] = useState(originalBorderColor);
  // const [offsetX, setOffsetX] = useState(0);
  // const [offsetY, setOffsetY] = useState(0);
  const [borderOffsetX, setBorderOffsetX] = useState(0);
  const [borderOffsetY, setBorderOffsetY] = useState(0);

  const [borderTop, setBorderTop] = useState(5);
  const [borderLeft, setBorderLeft] = useState(5);
  const [borderRight, setBorderRight] = useState(5);
  const [borderBottom, setBorderBottom] = useState(5);

  const borderMaterial = useMemo(() => {
    // const newPassMaterial = materialLoader(paperType, PAPER, "PAPER", 2);
    const newPassMaterial = new THREE.MeshStandardMaterial({
      side: THREE.DoubleSide,
      color: new THREE.Color(borderColor),
      roughness: 0.5,
      metalness: 0,
      envMapIntensity: 5,
      toneMapped: true,
    });
    const newBorderMaterial = new THREE.MeshStandardMaterial({
      side: THREE.DoubleSide,
      color: new THREE.Color(borderColor),
      roughness: 0.5,
      metalness: 0,
      envMapIntensity: 5,
      toneMapped: true,
    });

    const newBevelMaterial = new THREE.MeshStandardMaterial({
      side: THREE.DoubleSide,
      color: new THREE.Color(borderColor).multiplyScalar(BEVEL_DARKEN),
      roughness: 0.5,
      metalness: 0,
      envMapIntensity: 5,
      toneMapped: true,
    });

    return [newBorderMaterial, newPassMaterial, newBevelMaterial];
  }, []);

  /** FRAME TYPE SELECTOR */

  const [frameType, setFrameType] = useState("Print");
  const [glassThickness, setGlassThickness] = useState(0.2);

  /** BOX FRAME SETTINGS */

  const [boxProfileWidth, setBoxProfileWidth] = useState(2);
  const [boxProfileDepth, setBoxProfileDepth] = useState(5);
  const [glyphThickness, setGlyphThickness] = useState(1.2);
  const [borderThickness, setBorderThickness] = useState(0);
  const [borderSlit, setBorderSlit] = useState(0);
  const [glyphMat, setGlyphMat] = useState();
  const [glyphMatOption, setGlyphMatOption] = useState("Border");
  const [profileColor, setProfileColor] = useState("#8E9EB4");
  const [boxProfileMatType, setBoxProfileMatType] = useState("Color");
  const [glazing, setGlazing] = useState("Glass");
  const [glassType, setGlassType] = useState("Gloss");

  /** FLOAT FRAME SETTINGS */

  const [floatProfile, setFloatProfile] = useState("0.6 / 4");
  const [floatProfileWidth, setFloatProfileWidth] = useState(0.6);
  const [floatProfileDepth, setFloatProfileDepth] = useState(4);
  const [floatFinish, setFloatFinish] = useState("Paper");
  const [floatFinishDepth, setFloatFinishDepth] = useState(0);

  /** ALUMINUM FRAME SETTINGS */

  const [alumimunProfileType, setAluminumProfileType] = useState("273");
  const [glassInsert, setGlassInsert] = useState(0.5);
  const [aluminumProfileWidth, setAluminumProfileWidth] = useState(0.48);
  const [aluminumProfileDepth, setAluminumProfileDepth] = useState(1.95);
  const [aluminumProfileMatType, setAluminumProfileMatType] =
    useState("Polished Silver");

  /** FACE MOUNT SETTINGS */

  const [faceMountBackType, setFaceMountBackType] = useState("Dibond");
  const [faceMountFinish, setFaceMountFinish] = useState("Paper");
  const [chromaluxeType, setChromaluxeType] = useState("ChrMatt");
  const [faceMountFrame, setFaceMountFrame] = useState(false);
  const [perspexType, setPesrpexType] = useState("Gloss");

  /** CANVAS SETTINGS */

  const [canvasBorder, setCanvasBorder] = useState("Gallery Wrap");
  const [canvasFrame, setCanvasFrame] = useState(false);

  /** TONE MAPPING SETTINGS */
  const [printToneMapping, setPrintToneMapping] = useState(THREE.NoToneMapping);
  const [frameToneMapping, setFrameToneMapping] = useState(THREE.NoToneMapping);
  const [borderToneMapping, setBorderToneMapping] = useState(
    THREE.NoToneMapping
  );
  const [sceneToneMapping, setSceneToneMapping] = useState(THREE.NoToneMapping);

  //THREE.NoToneMapping
  //THREE.LinearToneMapping
  //THREE.ReinhardToneMapping
  //THREE.CineonToneMapping
  //THREE.ACESFilmicToneMapping
  //THREE.AgXToneMapping
  //THREE.NeutralToneMapping
  //THREE.CustomToneMapping

  const [printExposure, setPrintExposure] = useState(0.1);
  const [frameExposure, setFrameExposure] = useState(1.2);
  const [borderExposure, setBorderExposure] = useState(5);
  const [sceneExposure, setSceneExposure] = useState(1.2);

  /** MATERIALS */

  const [profileFrontMaterial, profileSideMaterial] = useMemo(() => {
    if (["Box", "Float", "Canvas"].includes(frameType)) {
      if (boxProfileMatType === "Color") {
        const mat = materialLoader(
          "Regular3",
          COLORS,
          "COLORS",
          1,
          profileColor
        );
        return [mat, mat];
      } else {
        const front = materialLoader(
          boxProfileMatType,
          WOODF,
          "WOODF",
          envIntensity,
          profileColor
        );
        const side = materialLoader(
          boxProfileMatType,
          WOOD,
          "WOOD",
          envIntensity,
          profileColor
        );

        return [front || null, side || null]; // Return null if material is not available
      }
    } else if (frameType === "Aluminum") {
      const front = materialLoader(
        aluminumProfileMatType,
        METALF,
        "METALF",
        envIntensity
      );
      const side = materialLoader(
        aluminumProfileMatType,
        METAL,
        "METAL",
        envIntensity
      );
      return [front || null, side || null]; // Return null if material is not available
    }

    return [null, null];
  }, [
    profileColor,
    frameType,
    boxProfileMatType,
    aluminumProfileMatType,
    envIntensity,
  ]);

  /** CAMERA FUNCTIONS */

  useEffect(() => {
    let totalWidth = printWidth;
    let totalHeight = printHeight;
    let depth = 0.1;

    totalWidth += 2 * borderWidth;
    totalHeight += 2 * borderHeight;

    switch (frameType) {
      case "Box":
        totalWidth += 2 * boxProfileWidth;
        totalHeight += 2 * boxProfileWidth;
        depth = boxProfileDepth;
        break;
      case "Float":
        totalWidth += 2 * floatProfileWidth;
        totalHeight += 2 * floatProfileWidth;
        depth = floatProfileDepth;
        break;
      case "Aluminum":
        totalWidth += 2 * aluminumProfileWidth;
        totalHeight += 2 * aluminumProfileWidth;
        depth = aluminumProfileDepth;
        break;
    }

    const geometry = new THREE.BoxGeometry(totalWidth, totalHeight, depth);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.0,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.z = depth / 2;
    setBoundingMesh(mesh);
  }, [
    printWidth,
    printHeight,
    borderWidth,
    borderHeight,
    frameType,
    boxProfileWidth,
    boxProfileDepth,
    floatProfileWidth,
    floatProfileDepth,
    aluminumProfileWidth,
    aluminumProfileDepth,
  ]);

  const onDetailCameraChange = useCallback(() => {
    setCameraDetailW(borderWidth / 2 + printWidth / 2);
    setCameraDetailH((borderHeight / 2 + printHeight / 2) * -1);
    setCameraDetailX(borderWidth / 2 + printWidth / 2 + 30);
    setCameraDetailY((borderHeight / 2 + printHeight / 2) * -1 + 20);
  }, [borderWidth, borderHeight, printWidth, printHeight]);

  const roomMesh = new THREE.Mesh(
    new THREE.BoxGeometry(350, 350, 350),
    new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.0,
    })
  );

  // const setMaterialMapOffsets = useCallback((material, offsetX, offsetY) => {
  //   if (!material) {
  //     console.warn("Material is undefined, cannot set map offsets.");
  //     return;
  //   }

  //   if (material.map) material.map.offset.set(offsetX, offsetY);
  //   if (material.normalMap) material.normalMap.offset.set(offsetX, offsetY);
  //   if (material.roughnessMap)
  //     material.roughnessMap.offset.set(offsetX, offsetY);
  // }, []);

  useEffect(() => {
    if (cameraType === "Detail") {
      onDetailCameraChange();
      // if (profileFrontMaterial)
      //   setMaterialMapOffsets(profileFrontMaterial, 1, 0);
      // if (profileSideMaterial) setMaterialMapOffsets(profileSideMaterial, 1, 0);
    } else {
      // const offset = 0.5;
      // if (profileFrontMaterial)
      //   setMaterialMapOffsets(profileFrontMaterial, offset, 0);
      // if (profileSideMaterial)
      //   setMaterialMapOffsets(profileSideMaterial, offset, 0);
    }
  }, [
    cameraType,
    profileFrontMaterial,
    profileSideMaterial,
    onDetailCameraChange,
    // setMaterialMapOffsets,
  ]);

  useEffect(() => {
    if (
      (frameType === "Float" || frameType === "Face Mount") &&
      faceMountBackType === "Chromaluxe"
    ) {
      setGlassThickness(0.001);
    } else {
      setGlassThickness(0.2);
    }
  }, [frameType, faceMountBackType]);

  // useEffect(() => {
  //   onDetailCameraChange();
  // }, [
  //   onDetailCameraChange,
  //   printWidth,
  //   printHeight,
  //   borderWidth,
  //   borderHeight,
  // ]);

  /** PRINT FUNCTIONS */

  const onImageChange = useCallback((imageData) => {
    // const { setImageLoaded, checkComplete } = useLoadingStore.getState();

    const normalizedSrc = normalizeImageUrlForCors(imageData);

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onerror = () => {
      console.error("Failed to load image:", imageData);
      // setImageLoaded(false);
    };

    img.onload = () => {
      console.log("Image loaded successfully");
      setImageFile(normalizedSrc);
      // setImageLoaded(true);
      // checkComplete();
    };

    img.src = normalizedSrc;
  }, []);

  const loadPaperMaterial = useCallback(
    (type) => {
      const imageMaterial = new THREE.MeshBasicMaterial({});

      const paperMaterial = new THREE.MeshStandardMaterial({});

      // Load and configure the image texture
      const imageTexture = withCrossOrigin(new THREE.TextureLoader()).load(
        imageFile
      );
      imageTexture.colorSpace = THREE.SRGBColorSpace;
      imageTexture.minFilter = THREE.LinearFilter;
      imageTexture.magFilter = THREE.LinearFilter;
      imageMaterial.map = imageTexture;

      // Load paper textures if available
      const paperTextureInfo = PAPER.find((p) => p.name === type);
      // console.log(paperTextureInfo);
      if (paperTextureInfo) {
        if (paperTextureInfo.roughnessMap) {
          const roughnessTexture = withCrossOrigin(new THREE.TextureLoader()).load(
            paperTextureInfo.roughnessMap
          );
          // paperMaterial.clearcoatMap = roughnessTexture;
          paperMaterial.roughnessMap = roughnessTexture;
          paperMaterial.roughnessMap.repeat.set(
            paperTextureInfo.size[0],
            paperTextureInfo.size[1]
          );
          paperMaterial.roughnessMap.wrapS = THREE.RepeatWrapping;
          paperMaterial.roughnessMap.wrapT = THREE.RepeatWrapping;
        }
        if (paperTextureInfo.normalMap) {
          const normalTexture = withCrossOrigin(new THREE.TextureLoader()).load(
            paperTextureInfo.normalMap
          );
          paperMaterial.normalMap = normalTexture;
          paperMaterial.normalMap.repeat.set(
            paperTextureInfo.size[0],
            paperTextureInfo.size[1]
          );
          paperMaterial.normalMap.wrapS = THREE.RepeatWrapping;
          paperMaterial.normalMap.wrapT = THREE.RepeatWrapping;
        }

        // paperMaterial.roughness = paperTextureInfo.roughness;
        paperMaterial.metalness = paperTextureInfo.metalness;
      }

      // Set paper material color to white (for border effects)

      setPaperMaterial([imageMaterial, paperMaterial]);
    },
    [imageFile, envIntensity]
  );

  useEffect(() => {
    loadPaperMaterial(paperType);
  }, [loadPaperMaterial, imageFile, paperType]);

  useEffect(() => {
    borderMaterial[0].color = new THREE.Color(borderColor);
    borderMaterial[1].color = new THREE.Color(borderColor);
    borderMaterial[2].color = new THREE.Color(borderColor).multiplyScalar(
      BEVEL_DARKEN
    );
  }, [borderColor]);

  useEffect(() => {
    const totalWidth = (borderLeft + borderRight) / 2;
    const totalHeight = (borderTop + borderBottom) / 2;

    // Calculate offsets
    const offsetX = (borderLeft - borderRight) / 2;
    const offsetY = (borderBottom - borderTop) / 2;

    // Update the context with new values
    setBorderWidth(totalWidth);
    setBorderHeight(totalHeight);
    setBorderOffsetX(offsetX);
    setBorderOffsetY(offsetY);
  }, [borderTop, borderLeft, borderRight, borderBottom]);

  /** FRAME TYPE FUNCTIONS */

  // const onFrameTypeChange = useCallback(
  //   (type) => {
  //     setFrameType(type);
  // switch (type) {
  //   case "Print":
  //     setFinishType(paperType);
  //     break;
  //   case "Box":
  //     setFinishType(paperType);
  //     setGlassInsert(0.6);
  //     break;
  //   case "Float":
  //     setFinishType(paperType);
  //     break;
  //   case "Aluminum":
  //     setFinishType(paperType);
  //     break;
  //   case "Face Mount":
  //     switch (faceMountBackType) {
  //       case "Dibond":
  //         setFinishType(paperType);
  //         break;
  //       case "Chromaluxe":
  //         setFinishType(chromaluxeType);
  //         break;
  //     }
  //     break;
  // }
  //   },
  //   [paperType]
  // );

  /** BOX FRAME FUNCTIONS */

  const onBorderTypeChange = useCallback((type) => {
    setBorderThickness(parseFloat(type));
    if (type == "0") {
      setBorderColor(originalBorderColor);
    }
  }, []);

  // useEffect(() => {
  //   if (frameType !== "Box" && frameType !== "Aluminum") {
  //     setBorderColor(originalBorderColor);
  //   }
  // }, [frameType]);

  useEffect(() => {
    let newGlyphMat;

    if (glyphMatOption == "Frame") {
      newGlyphMat = profileSideMaterial;
    } else if (glyphMatOption == "Border") {
      newGlyphMat = materialLoader(
        "Paper Matt",
        PAPER,
        envIntensity,
        profileColor
      );
      newGlyphMat.color = new THREE.Color(borderColor);
    }
    setGlyphMat(newGlyphMat);
  }, [borderColor, profileSideMaterial, glyphMatOption]);

  const onSetBoxProfilePreset = useCallback((type) => {
    switch (type) {
      case "1.2 / 3":
        setBoxProfileWidth(1.2);
        setBoxProfileDepth(3);
        break;
      case "1.2 / 4":
        setBoxProfileWidth(1.2);
        setBoxProfileDepth(4);
        break;
      case "1.5 / 3":
        setBoxProfileWidth(1.5);
        setBoxProfileDepth(3);
        break;
      case "1.5 / 4":
        setBoxProfileWidth(1.5);
        setBoxProfileDepth(4);
        break;
      case "2 / 4.5":
        setBoxProfileWidth(2);
        setBoxProfileDepth(4.5);
        break;
      case "2 / 6":
        setBoxProfileWidth(2);
        setBoxProfileDepth(6);
        break;
      case "3 / 4.5":
        setBoxProfileWidth(3);
        setBoxProfileDepth(4.5);
        break;
    }
  });

  /** FLOAT FRAME FUNCTIONS */

  const onFloatProfileChange = useCallback((type) => {
    setFloatProfile(type);
    switch (type) {
      case "0.6 / 4":
        setFloatProfileWidth(0.6);
        setFloatProfileDepth(4);
        break;
      case "0.8 / 4":
        setFloatProfileWidth(0.8);
        setFloatProfileDepth(4);
        break;
      case "1 / 4.5":
        setFloatProfileWidth(1);
        setFloatProfileDepth(4.5);
        break;
      case "2 / 4.5":
        setFloatProfileWidth(2);
        setFloatProfileDepth(4.5);
        break;
    }
  }, []);

  useEffect(() => {
    switch (faceMountFinish) {
      case "Paper":
        setFloatFinishDepth(0);
        break;
      case "Lamination":
        setFloatFinishDepth(0);
        break;
      case "Perspex":
        setFloatFinishDepth(0.2);
        break;
    }
  }, [faceMountFinish]);

  /** ALUMINUM FRAME FUNCTIONS */

  const onAluminumProfileTypeChange = useCallback((type) => {
    setAluminumProfileType(type);
    switch (type) {
      case "273":
        setAluminumProfileWidth(0.48);
        setAluminumProfileDepth(1.95);
        setGlassInsert(0.35);
        break;
      case "33":
        setAluminumProfileWidth(0.48);
        setAluminumProfileDepth(2.54);
        setGlassInsert(0.15);
        break;
      case "34":
        setAluminumProfileWidth(0.55);
        setAluminumProfileDepth(3.49);
        setGlassInsert(1.1);
        break;
      case "22":
        setAluminumProfileWidth(0.79);
        setAluminumProfileDepth(3.57);
        setGlassInsert(0.15);
        break;
    }
  }, []);

  /** FACE MOUNT FUNCTIONS */

  // const onFaceMountBackTypeChange = useCallback(
  //   (type) => {
  //     setFaceMountBackType(type);
  //     switch (type) {
  //       case "Dibond":
  //         setFinishType(paperType);
  //         break;
  //       case "Chromaluxe":
  //         setFinishType(chromaluxeType);
  //         break;
  //     }
  //   },
  //   [paperType, chromaluxeType]
  // );

  const onChromaluxeTypeChange = useCallback((type) => {
    setChromaluxeType(type);
    // setFinishType(type);

    switch (type) {
      case "ChrMetalicGlossy":
        setGlassType("Gloss");
        break;
      case "ChrMetalicMatt":
        setGlassType("Matt");
        break;
    }
  }, []);

  /** SCREENSHOT */

  const handleScreenshot = (event) => {
    setCameraType("Frame");

    setTimeout(() => {
      window.setFrameView?.();

      setTimeout(() => {
        const imageData = window.takeScreenshot(512); // or 1024, etc.

        event.source.postMessage(
          JSON.stringify({
            type: "screenshot-response",
            imageData,
          }),
          event.origin || "*"
        );
      }, 200);
    }, 0);
  };

  /** MEASUREMENTS GIZMO FUNCTIONS */

  // Values arriving from the parent are applied by updateFrameConfig without any
  // type or range checking, so coerce defensively here rather than trusting them.
  const onShowMeasurementsChange = useCallback((value) => {
    setShowMeasurements(value === true || value === "true");
  }, []);

  const onMeasurementsPositionChange = useCallback((value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      setMeasurementsPosition(25);
      return;
    }
    setMeasurementsPosition(Math.min(100, Math.max(0, parsed)));
  }, []);

  const onMeasurementsOrientationChange = useCallback((value) => {
    setMeasurementsOrientation(normalizeChoice(value, ORIENTATIONS, "vertical"));
  }, []);

  const onTotalWidthLabelPositionChange = useCallback((value) => {
    setTotalWidthLabelPosition(
      normalizeChoice(value, WIDTH_LABEL_POSITIONS, "below")
    );
  }, []);

  const onTotalHeightLabelPositionChange = useCallback((value) => {
    setTotalHeightLabelPosition(
      normalizeChoice(value, HEIGHT_LABEL_POSITIONS, "left")
    );
  }, []);

  const [frameConfig, setFrameConfig] = useState({
    generalSeetings: {
      units: "cm",
      wallColor: "#f9f5f0",
      cameraType: "Frame",
      frameViewPadding: 0.15,
      showMeasurements: false,
      measurementsPosition: 25,
      measurementsOrientation: "vertical",
      totalWidthLabelPosition: "below",
      totalHeightLabelPosition: "left",
    },
    printSettings: {
      imageFile: "textures/13.jpg",
      printWidth: 75,
      printHeight: 55,
      paperMaterial: paperMaterial,
      paperType: "Paper Matt",
      // finishType: "Paper Matt",
    },
    borderSettings: {
      borderWidth: 10,
      borderHeight: 10,
      borderTop: 5,
      borderRight: 5,
      borderBottom: 5,
      borderLeft: 5,
      borderColor: originalBorderColor,
      borderOffsetX: 0,
      borderOffsetY: 0,
      borderThickness: "0",
      borderSlit: 0,
    },
    frameTypeSettings: {
      frameType: "Print",
      glassThickness: 0.2,
      glassType: "Gloss",
      glazing: "Glass",
    },
    boxFrameSettings: {
      boxProfileWidth: 2,
      boxProfileDepth: 5,
      glyphThickness: 1.2,
      glyphMat: glyphMat,
      glyphMatOption: "Border",
      profileColor: "#8E9EB4",
      boxProfileMatType: "Color",
    },
    aluminumFrameSettings: {
      alumimunProfileType: "273",
      glassInsert: 0.5,
      aluminumProfileWidth: 0.48,
      aluminumProfileDepth: 1.95,
      aluminumProfileMatType: "Polished Silver",
    },
    floatFrameSettings: {
      floatProfile: "0.6 / 4",
      floatProfileWidth: 0.6,
      floatProfileDepth: 4,
      floatFinish: "Paper",
      floatFinishDepth: 0,
    },
    faceMountSettings: {
      faceMountBackType: "Dibond",
      faceMountFinish: "Paper",
      chromaluxeType: "ChrMatt",
      faceMountFrame: false,
      perspexType: "Gloss",
    },
    canvasSettings: {
      canvasBorder: "Gallery Wrap",
      canvasFrame: false,
    },
  });

  const propertyUpdateMap = useMemo(
    () => ({
      // 'generalSettings.units': onUnitsChange,
      "generalSettings.wallColor": setWallColor,
      "generalSettings.cameraType": setCameraType,
      "generalSettings.frameViewPadding": setFrameViewPadding,
      "generalSettings.maxFrameSize": setMaxFrameSize,
      "generalSettings.showMeasurements": onShowMeasurementsChange,
      "generalSettings.measurementsPosition": onMeasurementsPositionChange,
      "generalSettings.measurementsOrientation":
        onMeasurementsOrientationChange,
      "generalSettings.totalWidthLabelPosition": onTotalWidthLabelPositionChange,
      "generalSettings.totalHeightLabelPosition":
        onTotalHeightLabelPositionChange,
      "printSettings.imageFile": onImageChange,
      "printSettings.printWidth": setPrintWidth,
      "printSettings.printHeight": setPrintHeight,
      // 'printSettings.paperMaterial':
      "printSettings.paperType": setPaperType,
      // "printSettings.finishType": setFinishType,
      "borderSettings.borderWidth": setBorderWidth,
      "borderSettings.borderHeight": setBorderHeight,
      "borderSettings.borderTop": setBorderTop,
      "borderSettings.borderBottom": setBorderBottom,
      "borderSettings.borderLeft": setBorderLeft,
      "borderSettings.borderRight": setBorderRight,
      "borderSettings.borderColor": setBorderColor,
      "borderSettings.offsetX": setBorderOffsetX,
      "borderSettings.offsetY": setBorderOffsetY,
      "borderSettings.borderThickness": onBorderTypeChange,
      "borderSettings.borderSlit": setBorderSlit,
      "frameTypeSettings.frameType": setFrameType,
      "frameTypeSettings.glassThickness": setGlassThickness,
      "frameTypeSettings.glassType": setGlassType,
      "frameTypeSettings.glazing": setGlazing,
      "boxFrameSettings.boxProfileWidth": setBoxProfileWidth,
      "boxFrameSettings.boxProfileDepth": setBoxProfileDepth,
      "boxFrameSettings.boxProfilePreset": onSetBoxProfilePreset,
      "boxFrameSettings.glyphThickness": setGlyphThickness,
      // 'boxFrameSettings.glyphMat': ,
      "boxFrameSettings.glyphMatOption": setGlyphMatOption,
      "boxFrameSettings.profileColor": setProfileColor,
      "boxFrameSettings.boxProfileMatType": setBoxProfileMatType,
      "aluminumFrameSettings.alumimunProfileType": onAluminumProfileTypeChange,
      // 'aluminumFrameSettings.glassInsert': ,
      // 'aluminumFrameSettings.aluminumProfileWidth': ,
      // 'aluminumFrameSettings.aluminumProfileDepth': ,
      "aluminumFrameSettings.aluminumProfileMatType": setAluminumProfileMatType,
      "floatFrameSettings.floatProfile": onFloatProfileChange,
      "floatFrameSettings.floatProfileWidth": setFloatProfileWidth,
      "floatFrameSettings.floatProfileDepth": setFloatProfileDepth,
      "floatFrameSettings.floatFinish": setFloatFinish,
      "floatFrameSettings.floatFinishDepth": setFloatFinishDepth,
      "faceMountSettings.faceMountBackType": setFaceMountBackType,
      "faceMountSettings.faceMountFinish": setFaceMountFinish,
      "faceMountSettings.chromaluxeType": setChromaluxeType,
      "faceMountSettings.faceMountFrame": setFaceMountFrame,
      "faceMountSettings.perspexType": setPesrpexType,
      "canvasSettings.canvasBorder": setCanvasBorder,
      "canvasSettings.canvasFrame": setCanvasFrame,
    }),
    [
      setWallColor,
      onImageChange,
      setPrintWidth,
      setPrintHeight,
      setBorderWidth,
      setBorderHeight,
      setBorderColor,
      setBorderOffsetX,
      setBorderOffsetY,
      onBorderTypeChange,
      setBorderSlit,
      // onFrameTypeChange,
      setBoxProfileWidth,
      setBoxProfileDepth,
      setGlyphThickness,
      setGlyphMatOption,
      setProfileColor,
      setBoxProfileMatType,
      onAluminumProfileTypeChange,
      setAluminumProfileMatType,
      onFloatProfileChange,
      setCanvasBorder,
      setCanvasFrame,
      setBorderThickness,
      onShowMeasurementsChange,
      onMeasurementsPositionChange,
      onMeasurementsOrientationChange,
      onTotalWidthLabelPositionChange,
      onTotalHeightLabelPositionChange,
    ]
  );

  useEffect(() => {
    // Dispatch event when ControlProvider is mounted and ready
    console.log("ControlProvider mounted, dispatching ready event");
    window.dispatchEvent(new Event("control-provider-ready"));
  }, []); // Empty dependency array means this runs once on mount

  const updateFrameConfig = useCallback(
    (newData) => {
      setFrameConfig((prevConfig) => {
        const updateNestedConfig = (prev, newData, path) => {
          const updatedConfig = { ...prev };
          Object.keys(newData).forEach((key) => {
            const newPath = path ? `${path}.${key}` : key;
            if (
              typeof newData[key] === "object" &&
              newData[key] !== null &&
              !Array.isArray(newData[key])
            ) {
              updatedConfig[key] = updateNestedConfig(
                prev[key] || {},
                newData[key],
                newPath
              );
            } else if (newData[key] !== prev[key]) {
              updatedConfig[key] = newData[key];
              if (propertyUpdateMap[newPath]) {
                propertyUpdateMap[newPath](newData[key]);
              }
            }
          });
          return updatedConfig;
        };
        return updateNestedConfig(prevConfig, newData);
      });
    },
    [propertyUpdateMap]
  );

  const approvedOrigins = (import.meta.env.VITE_APPROVED_ORIGINS || "").split(
    ","
  );

  if (import.meta.env.DEV) {
    approvedOrigins.push("http://localhost:5173", "http://localhost:5174");
  }

  useEffect(() => {
    const handleMessage = (event) => {
      const messageEvent = event.detail; // Retrieve the original `postMessage` event
      if (!approvedOrigins.includes(messageEvent.origin)) {
        console.warn(
          "Received message from unapproved origin:",
          messageEvent.origin
        );
        return;
      }

      try {
        const data = JSON.parse(messageEvent.data);

        console.groupCollapsed("Received Message");
        console.log("Origin:", messageEvent.origin);
        console.log("Data:", JSON.stringify(data, null, 2));
        console.groupEnd();

        if (data.type === "screenshot-request") {
          handleScreenshot(messageEvent);
        } else {
          updateFrameConfig(data);
          // console.log("Settings applied:", data);
        }
      } catch (e) {
        console.error("Error processing message:", e);
      }
    };

    window.addEventListener("iframe-message", handleMessage);

    return () => window.removeEventListener("iframe-message", handleMessage);
  }, [updateFrameConfig]);

  // OVERRIDES
  // Glyph and Border Behavior

  const [pendingGlyphThickness, setPendingGlyphThickness] = useState(null);

  useEffect(() => {
    if (borderWidth === 0 && borderHeight === 0) {
      setGlyphMatOption("Frame");
    } else {
      setGlyphMatOption("Border");
    }

    if (borderThickness > 0) {
      setGlyphThickness(0);
    } else if (pendingGlyphThickness !== null) {
      setGlyphThickness(pendingGlyphThickness);
    }
  }, [borderWidth, borderHeight, borderThickness, pendingGlyphThickness]);

  // Capture the latest glyphThickness from postMessage
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.boxFrameSettings?.glyphThickness !== undefined) {
          setPendingGlyphThickness(data.boxFrameSettings.glyphThickness);
        }
      } catch (e) {
        console.error("Error processing message:", e);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // END OVERRIDES

  return (
    <ControlContext.Provider
      value={{
        imageFile,
        printWidth,
        printHeight,
        borderWidth,
        borderHeight,
        borderTop,
        borderBottom,
        borderLeft,
        borderRight,
        setBorderTop,
        setBorderBottom,
        setBorderLeft,
        setBorderRight,
        borderColor,
        setBorderColor,
        borderOffsetX,
        borderOffsetY,
        showMeasurements,
        measurementsPosition,
        measurementsOrientation,
        totalWidthLabelPosition,
        totalHeightLabelPosition,
        frameType,
        setProfileColor,
        profileColor,
        setBoxProfileMatType,
        // onFrameTypeChange,
        onImageChange,
        boxProfileWidth,
        boxProfileDepth,
        borderThickness,
        glyphMat,
        setFaceMountFinish,
        profileSideMaterial,
        profileFrontMaterial,
        onBorderTypeChange,
        borderSlit,
        setBorderSlit,
        glyphThickness,
        glyphMatOption,
        setGlyphMatOption,
        boxProfileMatType,
        floatProfileWidth,
        floatProfileDepth,
        floatProfile,
        onFloatProfileChange,
        floatFinish,
        floatFinishDepth,
        alumimunProfileType,
        onAluminumProfileTypeChange,
        glassInsert,
        aluminumProfileWidth,
        aluminumProfileDepth,
        // cameraZ,
        // setCameraZ,
        cameraDetailW,
        cameraDetailH,
        cameraDetailX,
        cameraDetailY,
        cameraType,
        faceMountBackType,
        // onFaceMountBackTypeChange,
        faceMountFinish,
        setAluminumProfileMatType,
        chromaluxeType,
        onChromaluxeTypeChange,
        glassThickness,
        paperMaterial,
        borderMaterial,
        aluminumProfileMatType,
        envIntensity,
        setEnvIntensity,
        canvasBorder,
        setCanvasBorder,
        canvasFrame,
        setCanvasFrame,
        wallColor,
        setWallColor,
        faceMountFrame,
        setFaceMountFrame,
        perspexType,
        setPesrpexType,
        glazing,
        setGlazing,
        glassType,
        setGlassType,
        // finishType,
        boundingMesh,
        roomMesh,
        setCameraType,
        // cameraControlsRef,
        // Tone mapping controls
        printToneMapping,
        setPrintToneMapping,
        frameToneMapping,
        setFrameToneMapping,
        borderToneMapping,
        setBorderToneMapping,
        sceneToneMapping,
        setSceneToneMapping,

        // Exposure controls
        printExposure,
        setPrintExposure,
        frameExposure,
        setFrameExposure,
        borderExposure,
        setBorderExposure,
        sceneExposure,
        setSceneExposure,
        frameViewPadding,
        imageLoaded,
        setGlyphThickness,
        maxFrameSize,
        paperType,
      }}
    >
      {children}
    </ControlContext.Provider>
  );

  return (
    <ControlContext.Provider value={{ ...config }}>
      {children}
    </ControlContext.Provider>
  );
};

export default ControlProvider;
